import React, { useState, useEffect } from "react";
import {
  Box,
  Paper,
  Typography,
  Card,
  CardContent,
  IconButton,
  styled,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Menu,
  MenuItem,
} from "@mui/material";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { NameCard } from "./NameCard";
import followUpService from "../../../apis/Business/FollowUpApi";
import { formatDateYYYYMMDD, parseDateFromYYYYMMDD } from "../../../apis/util";
import calendarIcon from "../../../assets/shapes_svg/ambition/actionplans/calendar-icon.svg";
import plusIcon from "../../../assets/shapes_svg/ambition/plus-icon.svg";
import timelinePlus from "../../../assets/shapes_svg/ambition/actionplans/timeline-plus.svg";
import timelineMinus from "../../../assets/shapes_svg/ambition/actionplans/timeline-minus.svg";
import leftArrow from "../../../assets/shapes_svg/ambition/Left_Arrow.svg";
import rightArrow from "../../../assets/shapes_svg/ambition/Right_Arrow.svg";
import { useUserAccess } from "../../../components/WriteAccessContext";

const TimelineContainer = styled(Paper)(({ theme }) => ({
  backgroundColor: "transparent",
  borderRadius: theme.spacing(2),
  position: "relative",
  overflow: "hidden",
  display: "flex",
  gap: "10px",
  marginTop: theme.spacing(4),
  padding: "0 20px 20px 0",
}));

const TimelineWrapper = styled(Box)(({ theme }) => ({
  position: "relative",
  display: "flex",
  flexWrap: "wrap",
  gap: theme.spacing(1),
  "&::before": {
    content: '""',
    position: "absolute",
    top: "17px",
    left: "0",
    right: "0",
    height: "2.83px",
    backgroundColor: "#1B3764",
    zIndex: 1,
    maxWidth: "92%",
  },
  "& > *:nth-of-type(-n+4)": {
    flex: "1 1 calc(25% - 24px)",
    maxWidth: "calc(25% - 24px)",
  },
  "& > *:nth-of-type(n+5)": {
    flex: "1 1 calc(25% - 24px)",
    maxWidth: "calc(25% - 24px)",
  },
}));

const TimelineStep = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  position: "relative",
  minWidth: "200px",
  zIndex: 2,
}));

const TimelineNode = () => {
  return (
    <img src={timelineMinus} alt="Timeline remove" width={25} height={25} />
  );
};

const StyledCard = styled(Card)(({ theme }) => ({
  width: "100%",
  marginTop: theme.spacing(2),
  backgroundColor: "rgba(255, 255, 255, 0.4)",
  color: "#1B3764",
  clipPath:
    "polygon(88.311% 98.239%,88.311% 98.239%,62.638% 98.161%,42.528% 98.29%,27.303% 98.406%,16.29% 98.289%,8.812% 97.717%,4.195% 96.471%,1.763% 94.329%,0.841% 91.073%,0.754% 86.48%,0.826% 80.332%,0.826% 80.332%,0.826% 72.834%,0.826% 64.209%,0.826% 54.898%,0.826% 45.343%,0.826% 35.987%,0.826% 27.271%,0.826% 19.637%,0.826% 13.526%,0.826% 9.381%,0.826% 7.644%,0.826% 7.644%,0.843% 7.66%,0.847% 7.652%,0.843% 7.616%,0.838% 7.547%,0.835% 7.441%,0.842% 7.293%,0.863% 7.1%,0.905% 6.856%,0.972% 6.557%,1.071% 6.199%,1.071% 6.199%,2.593% 3.902%,5.211% 2.323%,8.836% 1.34%,13.382% 0.833%,18.761% 0.681%,24.886% 0.762%,31.669% 0.955%,39.024% 1.139%,46.864% 1.194%,55.1% 0.997%,55.1% 0.997%,63.727% 1.002%,71.349% 0.939%,77.99% 0.894%,83.671% 0.951%,88.417% 1.196%,92.25% 1.715%,95.194% 2.592%,97.271% 3.913%,98.506% 5.763%,98.92% 8.228%,98.92% 8.228%,98.789% 31.874%,98.908% 50.909%,99.104% 65.842%,99.208% 77.181%,99.048% 85.436%,98.454% 91.115%,97.253% 94.726%,95.277% 96.778%,92.353% 97.78%,88.311% 98.239%)",
  "& .MuiCardContent-root": {
    padding: theme.spacing(2),
  },
}));

const AddButton = styled(IconButton)(({ theme }) => ({
  marginTop: theme.spacing(2),
  width: "100%",
  height: "40px",
  clipPath:
    "polygon(0.543% 39.196%, 0.552% 42.006%, 0.576% 44.653%, 0.608% 47.148%, 0.642% 49.502%, 0.674% 51.727%, 0.697% 53.833%, 0.705% 55.833%, 0.693% 57.738%, 0.655% 59.558%, 0.585% 61.305%, 0.563% 61.669%, 0.548% 61.982%, 0.54% 62.276%, 0.536% 62.584%, 0.538% 62.94%, 0.542% 63.376%, 0.55% 63.924%, 0.561% 64.619%, 0.572% 65.492%, 0.585% 66.576%, 0.587% 67.846%, 0.574% 69.355%, 0.553% 71.074%, 0.532% 72.974%, 0.516% 75.026%, 0.515% 77.2%, 0.535% 79.466%, 0.583% 81.796%, 0.668% 84.161%, 0.795% 86.53%, 0.851% 87.316%, 0.919% 88.173%, 1.001% 89.098%, 1.099% 90.089%, 1.215% 91.142%, 1.351% 92.254%, 1.51% 93.423%, 1.693% 94.646%, 1.902% 95.92%, 2.139% 97.243%, 2.326% 97.507%, 2.532% 97.783%, 2.758% 98.067%, 3.004% 98.355%, 3.268% 98.641%, 3.552% 98.923%, 3.854% 99.195%, 4.174% 99.454%, 4.513% 99.694%, 4.87% 99.912%, 13.152% 99.883%, 21.773% 99.859%, 30.665% 99.84%, 39.759% 99.826%, 48.99% 99.818%, 58.288% 99.816%, 67.587% 99.82%, 76.819% 99.832%, 85.916% 99.851%, 94.811% 99.878%, 95.269% 99.653%, 95.712% 99.413%, 96.137% 99.16%, 96.544% 98.898%, 96.932% 98.628%, 97.3% 98.355%, 97.647% 98.08%, 97.971% 97.806%, 98.272% 97.538%, 98.55% 97.276%, 98.665% 96.338%, 98.784% 95.313%, 98.904% 94.202%, 99.022% 93.009%, 99.138% 91.736%, 99.249% 90.386%, 99.352% 88.962%, 99.446% 87.467%, 99.53% 85.902%, 99.6% 84.271%, 99.629% 83.04%, 99.643% 81.95%, 99.644% 80.934%, 99.634% 79.923%, 99.615% 78.85%, 99.591% 77.647%, 99.562% 76.245%, 99.531% 74.577%, 99.501% 72.575%, 99.474% 70.17%, 99.462% 68.782%, 99.454% 67.353%, 99.448% 65.856%, 99.444% 64.262%, 99.443% 62.546%, 99.444% 60.68%, 99.448% 58.637%, 99.454% 56.39%, 99.462% 53.912%, 99.474% 51.175%, 99.485% 49.13%, 99.494% 47.481%, 99.501% 46.15%, 99.507% 45.059%, 99.51% 44.129%, 99.513% 43.282%, 99.515% 42.44%, 99.515% 41.525%, 99.516% 40.458%, 99.516% 39.162%, 99.516% 38.899%, 99.515% 38.152%, 99.512% 36.978%, 99.508% 35.437%, 99.5% 33.587%, 99.489% 31.488%, 99.473% 29.198%, 99.451% 26.777%, 99.424% 24.283%, 99.39% 21.775%, 99.379% 21.274%, 99.37% 20.782%, 99.362% 20.311%, 99.357% 19.87%, 99.353% 19.469%, 99.351% 19.119%, 99.349% 18.829%, 99.348% 18.609%, 99.348% 18.47%, 99.348% 18.421%, 99.335% 17.548%, 99.323% 16.841%, 99.311% 16.272%, 99.3% 15.816%, 99.29% 15.443%, 99.281% 15.129%, 99.274% 14.845%, 99.268% 14.564%, 99.265% 14.259%, 99.264% 13.903%, 99.246% 12.487%, 99.242% 11.279%, 99.245% 10.249%, 99.25% 9.365%, 99.253% 8.598%, 99.249% 7.918%, 99.231% 7.293%, 99.197% 6.694%, 99.139% 6.089%, 99.054% 5.45%, 98.963% 5.137%, 98.799% 4.84%, 98.575% 4.564%, 98.304% 4.313%, 97.998% 4.093%, 97.672% 3.91%, 97.336% 3.767%, 97.005% 3.67%, 96.691% 3.625%, 96.407% 3.636%, 96.015% 3.686%, 95.644% 3.717%, 95.294% 3.731%, 94.962% 3.73%, 94.648% 3.717%, 94.35% 3.695%, 94.068% 3.666%, 93.799% 3.634%, 93.543% 3.6%, 93.298% 3.567%, 93.231% 3.566%, 93.08% 3.563%, 92.845% 3.558%, 92.524% 3.551%, 92.117% 3.542%, 91.623% 3.53%, 91.041% 3.517%, 90.371% 3.502%, 89.611% 3.484%, 88.762% 3.465%, 87.353% 3.435%, 86.003% 3.407%, 84.72% 3.381%, 83.511% 3.358%, 82.382% 3.336%, 81.339% 3.317%, 80.391% 3.299%, 79.542% 3.284%, 78.802% 3.271%, 78.175% 3.259%, 76.115% 3.23%, 74.146% 3.205%, 72.278% 3.183%, 70.515% 3.164%, 68.865% 3.148%, 67.335% 3.134%, 65.931% 3.121%, 64.661% 3.109%, 63.531% 3.098%, 62.548% 3.088%, 62.143% 3.088%, 61.753% 3.088%, 61.325% 3.087%, 60.807% 3.086%, 60.148% 3.084%, 59.296% 3.081%, 58.2% 3.076%, 56.808% 3.071%, 55.067% 3.063%, 52.928% 3.054%, 49.403% 3.045%, 46.087% 3.038%, 43.019% 3.033%, 40.237% 3.031%, 37.778% 3.033%, 35.681% 3.037%, 33.984% 3.044%, 32.725% 3.055%, 31.941% 3.07%, 31.671% 3.088%, 31.388% 3.105%, 30.538% 3.114%, 29.122% 3.118%, 27.14% 3.118%, 24.593% 3.114%, 21.481% 3.108%, 17.806% 3.101%, 13.567% 3.095%, 8.764% 3.09%, 3.399% 3.088%, 3.284% 3.086%, 3.143% 3.084%, 2.98% 3.093%, 2.8% 3.121%, 2.607% 3.178%, 2.404% 3.273%, 2.198% 3.415%, 1.991% 3.614%, 1.788% 3.878%, 1.593% 4.218%, 1.305% 4.922%, 1.111% 5.734%, 0.993% 6.677%, 0.934% 7.773%, 0.916% 9.043%, 0.922% 10.511%, 0.934% 12.198%, 0.936% 14.127%, 0.909% 16.319%, 0.837% 18.798%, 0.756% 21.116%, 0.649% 23.255%, 0.53% 25.259%, 0.413% 27.171%, 0.312% 29.035%, 0.241% 30.897%, 0.215% 32.8%, 0.247% 34.788%, 0.352% 36.905%, 0.543% 39.196%)",
  backgroundColor: "rgba(255, 255, 255, 0.4)",
  borderRadius: theme.spacing(1),
  "&:hover": {
    backgroundColor: "rgba(255, 255, 255, 0.3)",
  },
  cursor: "pointer",
}));

const NavigationButton = styled(IconButton)(({ theme }) => ({
  position: "absolute",
  top: "50%",
  transform: "translateY(-50%)",
  backgroundColor: "#fff",
  boxShadow: theme.shadows[2],
  "&:hover": {
    backgroundColor: "#f5f5f5",
  },
  zIndex: 3,
}));

const DatePickerWrapper = styled(Box)(({ theme }) => ({
  "& .MuiTextField-root": {
    backgroundColor: "rgba(255, 255, 255, 0.3)",
    width: "120px",
    "& .MuiInputBase-root": {
      height: "40px",
    },
  },
}));

const AddItemDialog = ({ open, onClose, onAdd, milestoneIndex }) => {
  const [itemData, setItemData] = useState({
    what: "",
    host: "",
    where: "",
    link: "",
    additionalInfo: "",
  });

  const handleChange = (field, value) => {
    setItemData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSubmit = () => {
    onAdd(milestoneIndex, itemData);
    onClose();
    setItemData({
      what: "",
      host: "",
      where: "",
      link: "",
      additionalInfo: "",
    });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Add New Item</DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="What"
              value={itemData.what}
              onChange={(e) => handleChange("what", e.target.value)}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Host"
              value={itemData.host}
              onChange={(e) => handleChange("host", e.target.value)}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Where"
              value={itemData.where}
              onChange={(e) => handleChange("where", e.target.value)}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Link"
              value={itemData.link}
              onChange={(e) => handleChange("link", e.target.value)}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              rows={2}
              label="Additional Info"
              value={itemData.additionalInfo}
              onChange={(e) => handleChange("additionalInfo", e.target.value)}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSubmit} variant="contained">
          Add Item
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const FollowUpSection = ({ effortId, initialData }) => {
  const [timelines, setTimelines] = useState([]);
  const [currentTimelineId, setCurrentTimelineId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const milestoneTypes = [{ value: "Follow Up", follow_up: "Follow Up" }];
  const [refreshTimelines, setRefreshTimelines] = useState(false);
  const { userAccess } = useUserAccess();

  const [timelineData, setTimelineData] = useState(
    initialData?.steps || [
      {
        follow_up: "Follow Up",
        type: "Follow Up",
        date: new Date(),
        items: [],
      },
    ]
  );

  useEffect(() => {
    const fetchTimelines = async () => {
      try {
        setLoading(true);

        // Fetch all timelines
        const fetchedTimelines = await followUpService.getFollowUp(effortId);

        // If fetched timelines exist and are non-empty, proceed to fetch milestones
        if (fetchedTimelines && fetchedTimelines.length > 0) {
          // Fetch milestones for all timelines in parallel
          const milestonesPromises = fetchedTimelines.map((timeline) =>
            followUpService.milestones.getMilestones(effortId, timeline.id)
          );
          const milestonesResults = await Promise.all(milestonesPromises);

          // Transform data to include milestones for each timeline
          const transformedData = fetchedTimelines.map((timeline, index) => ({
            id: timeline.id,
            follow_up: timeline.follow_up,
            type: timeline.type || "milestone",
            date: new Date(timeline.date),
            items: milestonesResults[index].map((m) => ({
              what: m.what,
              host: m.host,
              where: m.where,
              link: m.link,
              additionalInfo: m.additional_info,
            })),
          }));

          // Sort the transformedData by date in ascending order
          const sortedData = transformedData.sort((a, b) => a.date - b.date);

          // Update state with fetched and sorted data
          setTimelines(fetchedTimelines);
          setTimelineData(sortedData);
        } else {
          // If no timelines are fetched, set to initial data
          setTimelineData(
            initialData?.steps || [
              {
                follow_up: "Follow Up",
                type: "Follow Up",
                date: new Date(),
                items: [],
              },
            ]
          );
        }
      } catch (err) {
        console.error("Error fetching timelines for effortId:", effortId, err);
        setError(err);

        // If fetching fails, set to initial data
        setTimelineData(
          initialData?.steps || [
            {
              follow_up: "Follow Up",
              type: "Follow Up",
              date: new Date(),
              items: [],
            },
          ]
        );
      } finally {
        setLoading(false);
      }
    };

    if (effortId) {
      fetchTimelines();
    }
  }, [effortId, refreshTimelines, initialData]);

  const [addItemDialogOpen, setAddItemDialogOpen] = useState(false);
  const [selectedMilestoneIndex, setSelectedMilestoneIndex] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [editingMilestone, setEditingMilestone] = useState(null);
  const [isAddingNew, setIsAddingNew] = useState(false);
  const [currentAccessedTimeline, setCurrentAccessedTimeline] = useState(false);

  const itemsPerPage = 4;
  const totalPages = Math.ceil(timelineData.length / itemsPerPage);
  const showNavigation = timelineData.length > itemsPerPage;

  const visibleTimelineData = timelineData.slice(
    currentPage * itemsPerPage,
    (currentPage + 1) * itemsPerPage
  );

  const handlePrevPage = () => {
    setCurrentPage((prev) => Math.max(0, prev - 1));
  };

  const handleNextPage = () => {
    setCurrentPage((prev) => Math.min(totalPages - 1, prev + 1));
  };

  // Create Timeline
  const handleCreateTimeline = async (newTimelineData) => {
    try {
      let formattedDate;
      if (newTimelineData.date) {
        formattedDate = formatDateYYYYMMDD(newTimelineData.date);
      }
      const createdTimeline = await followUpService.createFollowUp(effortId, {
        follow_up: newTimelineData.follow_up,
        date: formattedDate,
      });

      // Update local state
      setTimelines((prev) => [...prev, createdTimeline]);
      setCurrentTimelineId(createdTimeline.id);

      // Update timelineData
      setTimelineData((prev) => [
        ...prev,
        {
          id: createdTimeline.id,
          follow_up: createdTimeline.follow_up,
          type: newTimelineData.type,
          date: new Date(createdTimeline.date),
          items: [],
        },
      ]);

      // Trigger a re-fetch of timelines
      setRefreshTimelines((prev) => !prev);
    } catch (err) {
      console.error("Error creating follow up:", err);
      setError(err);
    }
  };

  // Update Timeline
  const handleUpdateTimeline = async (updatedTimelineData) => {
    try {
      const existingTimelineData = timelineData.find(
        (tl) => tl.id === updatedTimelineData.id
      );

      // Construct the payload correctly
      const payload = {
        follow_up:
          updatedTimelineData.follow_up || existingTimelineData.follow_up,
        date: updatedTimelineData.date,
      };

      // Call the API
      await followUpService.updateFollowUp(
        effortId,
        updatedTimelineData.id,
        payload
      );

      // Update local state
      setTimelineData((prev) =>
        prev.map((timeline) =>
          timeline.id === updatedTimelineData.id
            ? updatedTimelineData
            : timeline
        )
      );
    } catch (err) {
      console.error("Error updating follow up:", err);
      setError(err);
    }
  };

  // Update Milestone Method
  const handleUpdateMilestone = async (
    timelineId,
    milestoneId,
    milestoneData
  ) => {
    try {
      const updatedMilestone = await followUpService.milestones.updateMilestone(
        effortId,
        timelineId,
        milestoneId,
        {
          what: milestoneData.what,
          host: milestoneData.host,
          where: milestoneData.where,
          link: milestoneData.link,
          additionalInfo: milestoneData.additionalInfo,
        }
      );

      // Update local state
      setTimelineData((prev) =>
        prev.map((timeline) =>
          timeline.id === timelineId
            ? {
                ...timeline,
                items: timeline.items.map((item) =>
                  item.id === milestoneId ? updatedMilestone : item
                ),
              }
            : timeline
        )
      );

      return updatedMilestone;
    } catch (err) {
      console.error("Error updating milestone:", err);
      setError(err);
    }
  };

  const handleDateChange = async (newDate, index) => {
    const actualIndex = currentPage * itemsPerPage + index; // Calculate the actual index in the timelineData array
    const currentTimeline = timelineData[actualIndex]; // Use the actual index to get the current timeline

    if (!currentTimeline) {
      console.error("No follow up found at index:", actualIndex);
      return; // Exit if no timeline is found
    }

    let formattedDate;
    if (newDate) {
      formattedDate = formatDateYYYYMMDD(newDate); // Format the date for the backend
    }

    let timelineToUpdate;
    // If timeline doesn't have an ID, create a new timeline
    if (!currentTimeline.id) {
      timelineToUpdate = await handleCreateTimeline({
        follow_up: currentTimeline.follow_up || "New Follow Up",
        type: currentTimeline.type || "milestone",
        date: formattedDate,
      });
    } else {
      timelineToUpdate = {
        ...currentTimeline,
        date: formattedDate,
      };
      await handleUpdateTimeline(timelineToUpdate);
    }

    // Update local state
    const newData = [...timelineData];
    newData[actualIndex] = timelineToUpdate;

    setTimelineData(newData);
  };
  const handleAddItem = (index) => {
    setSelectedMilestoneIndex(currentPage * itemsPerPage + index);
    setAddItemDialogOpen(true);
  };

  const handleAddItemSubmit = async (milestoneIndex, itemData) => {
    try {
      const timeline = timelineData[milestoneIndex];

      const createdMilestone = await followUpService.milestones.createMilestone(
        effortId,
        timeline.id,
        {
          what: itemData.what,
          host: itemData.host,
          where: itemData.where,
          link: itemData.link,
          additionalInfo: itemData.additionalInfo,
        }
      );

      // Update local state
      const newData = [...timelineData];
      newData[milestoneIndex].items = [
        ...(newData[milestoneIndex].items || []),
        {
          id: createdMilestone.id,
          ...itemData,
        },
      ];

      setTimelineData(newData);
    } catch (err) {
      console.error("Error creating milestone:", err);
      setError(err);
    }
  };

  // Delete Milestone
  const handleDeleteMilestone = async (timelineId, milestoneId) => {
    try {
      await followUpService.milestones.deleteMilestone(
        effortId,
        timelineId,
        milestoneId
      );

      // Update local state to remove milestone
      setTimelineData((prev) =>
        prev.map((timeline) => ({
          ...timeline,
          items: timeline.items.filter((item) => item.id !== milestoneId),
        }))
      );
    } catch (err) {
      console.error("Error deleting milestone:", err);
      setError(err);
    }
  };

  // Delete Timeline
  const handleDeleteFollowUp = async (timelineId) => {
    try {
      await followUpService.deleteFollowUp(effortId, timelineId);

      // Remove timeline from local state
      setTimelines((prev) => prev.filter((t) => t.id !== timelineId));
      setTimelineData((prev) => prev.filter((t) => t.id !== timelineId));
    } catch (err) {
      console.error("Error deleting follow up:", err);
      setError(err);
    }
  };

  const handleNodeClick = (event, milestone) => {
    setIsAddingNew(false);
    setEditingMilestone(true);
    setCurrentAccessedTimeline(milestone);
    setAnchorEl(event.currentTarget);
  };

  const handleAddNewClick = (event) => {
    event.stopPropagation(); // Prevent the node click event from firing
    setIsAddingNew(true);
    setEditingMilestone(false);
    setAnchorEl(event.currentTarget);
  };

  const handleMilestoneSelect = async (type) => {
    try {
      const isNewTimeline =
        !currentAccessedTimeline || !currentAccessedTimeline.id;
      if (isNewTimeline || isAddingNew) {
        // Create a new timeline
        const newTimelineData = {
          follow_up: type, // Ensure the timeline field is included
          type,
          date: formatDateYYYYMMDD(new Date()), // Format the date correctly
        };

        await handleCreateTimeline(newTimelineData);
      } else {
        // Update an existing timeline
        const updatedTimelineData = {
          id: currentAccessedTimeline.id,
          follow_up: type ? type : currentAccessedTimeline.follow_up, // Ensure the timeline field is included
          type,
          date: currentAccessedTimeline.date
            ? formatDateYYYYMMDD(currentAccessedTimeline.date) // Format the date correctly
            : formatDateYYYYMMDD(new Date()),
        };

        await handleUpdateTimeline(updatedTimelineData);
      }
    } catch (error) {
      console.error("Error in handleMilestoneSelect:", error);
      setError(error);
    } finally {
      // Reset state
      setAnchorEl(null);
      setEditingMilestone(false);
      setCurrentAccessedTimeline(null);
      setIsAddingNew(false);
    }
  };

  const CalendarComponent = () => {
    return (
      <img src={calendarIcon} alt="Calendar Icon" width={22} height={22} />
    );
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <TimelineContainer elevation={0}>
        <NameCard
          deptName={"Follow Up"}
          isBottom={false}
          className={"other-effort-container"}
        />

        <Box
          sx={{ position: "relative", width: "100%" }}
          className="action-plans-parent-bubble"
        >
          {showNavigation && (
            <>
              <NavigationButton
                onClick={handlePrevPage}
                disabled={currentPage === 0}
                sx={{ left: -10 }}
              >
                <img src={leftArrow} width={21} height={37} alt="Left Arrow" />
              </NavigationButton>
              <NavigationButton
                onClick={handleNextPage}
                disabled={currentPage >= totalPages - 1}
                sx={{ right: "60px" }}
              >
                <img src={rightArrow} width={21} height={37} alt="Left Arrow" />
              </NavigationButton>
            </>
          )}
          <TimelineWrapper>
            {visibleTimelineData.map((milestone, index) => {
              return (
                <TimelineStep key={index}>
                  {index === visibleTimelineData.length - 1 &&
                  currentPage === totalPages - 1 &&
                  timelineData.length < 12 ? (
                    <IconButton
                      size="small"
                      onClick={handleAddNewClick}
                      disabled={userAccess !== "write"}
                    >
                      <img
                        src={timelinePlus}
                        alt="Add new timeline"
                        width={25}
                        height={25}
                      />
                    </IconButton>
                  ) : (
                    <IconButton
                      size="small"
                      onClick={() => handleDeleteFollowUp(milestone.id)}
                      disabled={userAccess !== "write"}
                    >
                      <TimelineNode data-index={index} />
                    </IconButton>
                  )}

                  <Typography
                    variant="subtitle2"
                    gutterBottom
                    onClick={(e) => {
                      if (userAccess === "write") {
                        handleNodeClick(e, milestone);
                      }
                    }}
                    className="timeline-title-bg"
                    sx={{
                      cursor: "pointer",
                      backgroundColor: "rgba(255,255,255,0.4)",
                    }}
                  >
                    {milestone ? milestone.follow_up : "Follow Up"}
                  </Typography>

                  <DatePickerWrapper>
                    <DatePicker
                      value={
                        milestone
                          ? parseDateFromYYYYMMDD(milestone.date)
                          : new Date()
                      }
                      disabled={userAccess !== "write"}
                      onChange={(newDate) =>
                        handleDateChange(newDate, index, milestone.id)
                      }
                      format="MM-dd-yyyy"
                      slotProps={{
                        textField: {
                          size: "small",
                          sx: {
                            backgroundColor: "transparent",
                            border: "none",
                            outline: "none",
                            color: "#1B3764", // Text color for the input
                            "& .MuiOutlinedInput-notchedOutline": {
                              border: "none",
                            },
                            "&:focus": {
                              outline: "none",
                            },
                            // Target the input field specifically
                            "& .css-19qh8xo-MuiInputBase-input-MuiOutlinedInput-input":
                              {
                                color: "#1B3764", // Change this to your desired text color
                              },
                          },
                        },
                      }}
                      slots={{
                        openPickerIcon: CalendarComponent,
                      }}
                      className="timeline-title-bg"
                      sx={{
                        clipPath:
                          "polygon(0.543% 39.196%, 0.552% 42.006%, 0.576% 44.653%, 0.608% 47.148%, 0.642% 49.502%, 0.674% 51.727%, 0.697% 53.833%, 0.705% 55.833%, 0.693% 57.738%, 0.655% 59.558%, 0.585% 61.305%, 0.563% 61.669%, 0.548% 61.982%, 0.54% 62.276%, 0.536% 62.584%, 0.538% 62.94%, 0.542% 63.376%, 0.55% 63.924%, 0.561% 64.619%, 0.572% 65.492%, 0.585% 66.576%, 0.587% 67.846%, 0.574% 69.355%, 0.553% 71.074%, 0.532% 72.974%, 0.516% 75.026%, 0.515% 77.2%, 0.535% 79.466%, 0.583% 81.796%, 0.668% 84.161%, 0.795% 86.53%, 0.851% 87.316%, 0.919% 88.173%, 1.001% 89.098%, 1.099% 90.089%, 1.215% 91.142%, 1.351% 92.254%, 1.51% 93.423%, 1.693% 94.646%, 1.902% 95.92%, 2.139% 97.243%, 2.326% 97.507%, 2.532% 97.783%, 2.758% 98.067%, 3.004% 98.355%, 3.268% 98.641%, 3.552% 98.923%, 3.854% 99.195%, 4.174% 99.454%, 4.513% 99.694%, 4.87% 99.912%, 13.152% 99.883%, 21.773% 99.859%, 30.665% 99.84%, 39.759% 99.826%, 48.99% 99.818%, 58.288% 99.816%, 67.587% 99.82%, 76.819% 99.832%, 85.916% 99.851%, 94.811% 99.878%, 95.269% 99.653%, 95.712% 99.413%, 96.137% 99.16%, 96.544% 98.898%, 96.932% 98.628%, 97.3% 98.355%, 97.647% 98.08%, 97.971% 97.806%, 98.272% 97.538%, 98.55% 97.276%, 98.665% 96.338%, 98.784% 95.313%, 98.904% 94.202%, 99.022% 93.009%, 99.138% 91.736%, 99.249% 90.386%, 99.352% 88.962%, 99.446% 87.467%, 99.53% 85.902%, 99.6% 84.271%, 99.629% 83.04%, 99.643% 81.95%, 99.644% 80.934%, 99.634% 79.923%, 99.615% 78.85%, 99.591% 77.647%, 99.562% 76.245%, 99.531% 74.577%, 99.501% 72.575%, 99.474% 70.17%, 99.462% 68.782%, 99.454% 67.353%, 99.448% 65.856%, 99.444% 64.262%, 99.443% 62.546%, 99.444% 60.68%, 99.448% 58.637%, 99.454% 56.39%, 99.462% 53.912%, 99.474% 51.175%, 99.485% 49.13%, 99.494% 47.481%, 99.501% 46.15%, 99.507% 45.059%, 99.51% 44.129%, 99.513% 43.282%, 99.515% 42.44%, 99.515% 41.525%, 99.516% 40.458%, 99.516% 39.162%, 99.516% 38.899%, 99.515% 38.152%, 99.512% 36.978%, 99.508% 35.437%, 99.5% 33.587%, 99.489% 31.488%, 99.473% 29.198%, 99.451% 26.777%, 99.424% 24.283%, 99.39% 21.775%, 99.379% 21.274%, 99.37% 20.782%, 99.362% 20.311%, 99.357% 19.87%, 99.353% 19.469%, 99.351% 19.119%, 99.349% 18.829%, 99.348% 18.609%, 99.348% 18.47%, 99.348% 18.421%, 99.335% 17.548%, 99.323% 16.841%, 99.311% 16.272%, 99.3% 15.816%, 99.29% 15.443%, 99.281% 15.129%, 99.274% 14.845%, 99.268% 14.564%, 99.265% 14.259%, 99.264% 13.903%, 99.246% 12.487%, 99.242% 11.279%, 99.245% 10.249%, 99.25% 9.365%, 99.253% 8.598%, 99.249% 7.918%, 99.231% 7.293%, 99.197% 6.694%, 99.139% 6.089%, 99.054% 5.45%, 98.963% 5.137%, 98.799% 4.84%, 98.575% 4.564%, 98.304% 4.313%, 97.998% 4.093%, 97.672% 3.91%, 97.336% 3.767%, 97.005% 3.67%, 96.691% 3.625%, 96.407% 3.636%, 96.015% 3.686%, 95.644% 3.717%, 95.294% 3.731%, 94.962% 3.73%, 94.648% 3.717%, 94.35% 3.695%, 94.068% 3.666%, 93.799% 3.634%, 93.543% 3.6%, 93.298% 3.567%, 93.231% 3.566%, 93.08% 3.563%, 92.845% 3.558%, 92.524% 3.551%, 92.117% 3.542%, 91.623% 3.53%, 91.041% 3.517%, 90.371% 3.502%, 89.611% 3.484%, 88.762% 3.465%, 87.353% 3.435%, 86.003% 3.407%, 84.72% 3.381%, 83.511% 3.358%, 82.382% 3.336%, 81.339% 3.317%, 80.391% 3.299%, 79.542% 3.284%, 78.802% 3.271%, 78.175% 3.259%, 76.115% 3.23%, 74.146% 3.205%, 72.278% 3.183%, 70.515% 3.164%, 68.865% 3.148%, 67.335% 3.134%, 65.931% 3.121%, 64.661% 3.109%, 63.531% 3.098%, 62.548% 3.088%, 62.143% 3.088%, 61.753% 3.088%, 61.325% 3.087%, 60.807% 3.086%, 60.148% 3.084%, 59.296% 3.081%, 58.2% 3.076%, 56.808% 3.071%, 55.067% 3.063%, 52.928% 3.054%, 49.403% 3.045%, 46.087% 3.038%, 43.019% 3.033%, 40.237% 3.031%, 37.778% 3.033%, 35.681% 3.037%, 33.984% 3.044%, 32.725% 3.055%, 31.941% 3.07%, 31.671% 3.088%, 31.388% 3.105%, 30.538% 3.114%, 29.122% 3.118%, 27.14% 3.118%, 24.593% 3.114%, 21.481% 3.108%, 17.806% 3.101%, 13.567% 3.095%, 8.764% 3.09%, 3.399% 3.088%, 3.284% 3.086%, 3.143% 3.084%, 2.98% 3.093%, 2.8% 3.121%, 2.607% 3.178%, 2.404% 3.273%, 2.198% 3.415%, 1.991% 3.614%, 1.788% 3.878%, 1.593% 4.218%, 1.305% 4.922%, 1.111% 5.734%, 0.993% 6.677%, 0.934% 7.773%, 0.916% 9.043%, 0.922% 10.511%, 0.934% 12.198%, 0.936% 14.127%, 0.909% 16.319%, 0.837% 18.798%, 0.756% 21.116%, 0.649% 23.255%, 0.53% 25.259%, 0.413% 27.171%, 0.312% 29.035%, 0.241% 30.897%, 0.215% 32.8%, 0.247% 34.788%, 0.352% 36.905%, 0.543% 39.196%)",
                        backgroundColor: "rgba(255, 255, 255, 0.3)",
                      }}
                    />
                  </DatePickerWrapper>

                  {milestone &&
                    milestone.items &&
                    milestone.items.map((item, itemIndex) => (
                      <StyledCard key={itemIndex} elevation={1}>
                        <CardContent>
                          <Typography variant="subtitle2" gutterBottom>
                            What: {item.what}
                          </Typography>
                          <Typography variant="body2" gutterBottom>
                            Host: {item.host}
                          </Typography>
                          <Typography variant="body2" gutterBottom>
                            Where: {item.where}
                          </Typography>
                          {item.link && (
                            <Typography variant="body2" gutterBottom>
                              {item.link}
                            </Typography>
                          )}
                          {item.additionalInfo && (
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{ fontSize: "0.75rem" }}
                            >
                              {item.additionalInfo}
                            </Typography>
                          )}
                        </CardContent>
                      </StyledCard>
                    ))}

                  <AddButton
                    onClick={() => handleAddItem(index)}
                    disabled={userAccess !== "write"}
                  >
                    <img
                      src={plusIcon}
                      alt="Plus icon"
                      width={25}
                      height={25}
                    />
                  </AddButton>
                </TimelineStep>
              );
            })}
          </TimelineWrapper>
        </Box>

        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={() => {
            setAnchorEl(null);
            setEditingMilestone(false);
            setIsAddingNew(false);
          }}
          PaperProps={{
            style: {
              width: 150,
              clipPath:
                "polygon(0.54% 37.608%,0.54% 37.608%,0.549% 40.481%,0.572% 43.186%,0.604% 45.736%,0.639% 48.142%,0.67% 50.416%,0.693% 52.57%,0.702% 54.614%,0.69% 56.561%,0.651% 58.421%,0.581% 60.207%,0.581% 60.207%,0.56% 60.579%,0.545% 60.898%,0.537% 61.199%,0.533% 61.514%,0.534% 61.878%,0.539% 62.323%,0.547% 62.884%,0.557% 63.593%,0.569% 64.486%,0.581% 65.595%,0.581% 65.595%,0.584% 66.892%,0.571% 68.434%,0.55% 70.192%,0.528% 72.134%,0.513% 74.231%,0.512% 76.453%,0.532% 78.77%,0.58% 81.151%,0.664% 83.568%,0.791% 85.989%,0.791% 85.989%,0.846% 86.793%,0.914% 87.669%,0.995% 88.615%,1.093% 89.627%,1.209% 90.703%,1.345% 91.84%,1.503% 93.035%,1.685% 94.285%,1.893% 95.588%,2.129% 96.939%,2.129% 96.939%,2.315% 97.209%,2.52% 97.491%,2.745% 97.782%,2.99% 98.076%,3.253% 98.369%,3.535% 98.657%,3.836% 98.935%,4.155% 99.199%,4.493% 99.445%,4.848% 99.667%,4.848% 99.667%,13.094% 99.638%,21.677% 99.613%,30.53% 99.594%,39.584% 99.58%,48.774% 99.571%,58.032% 99.569%,67.29% 99.574%,76.481% 99.586%,85.538% 99.605%,94.394% 99.633%,94.394% 99.633%,94.851% 99.403%,95.292% 99.158%,95.715% 98.9%,96.121% 98.631%,96.507% 98.356%,96.873% 98.076%,97.218% 97.795%,97.541% 97.516%,97.841% 97.241%,98.117% 96.973%,98.117% 96.973%,98.232% 96.015%,98.35% 94.966%,98.47% 93.831%,98.588% 92.612%,98.703% 91.311%,98.813% 89.931%,98.916% 88.476%,99.01% 86.947%,99.093% 85.348%,99.163% 83.68%,99.163% 83.68%,99.192% 82.422%,99.206% 81.308%,99.207% 80.27%,99.197% 79.237%,99.179% 78.14%,99.154% 76.91%,99.125% 75.477%,99.095% 73.772%,99.065% 71.726%,99.038% 69.268%,99.038% 69.268%,99.026% 67.849%,99.018% 66.389%,99.011% 64.858%,99.008% 63.229%,99.006% 61.475%,99.008% 59.568%,99.011% 57.48%,99.018% 55.183%,99.026% 52.65%,99.038% 49.852%,99.038% 49.852%,99.049% 47.762%,99.058% 46.077%,99.065% 44.716%,99.07% 43.601%,99.074% 42.65%,99.077% 41.785%,99.078% 40.924%,99.079% 39.989%,99.079% 38.899%,99.08% 37.573%,99.08% 37.573%,99.079% 37.305%,99.079% 36.541%,99.076% 35.342%,99.071% 33.766%,99.064% 31.876%,99.052% 29.73%,99.036% 27.39%,99.015% 24.915%,98.988% 22.366%,98.954% 19.802%,98.954% 19.802%,98.943% 19.29%,98.934% 18.788%,98.927% 18.306%,98.921% 17.855%,98.918% 17.445%,98.915% 17.087%,98.913% 16.791%,98.913% 16.566%,98.912% 16.424%,98.912% 16.374%,98.912% 16.374%,98.9% 15.481%,98.888% 14.759%,98.876% 14.178%,98.865% 13.711%,98.855% 13.33%,98.846% 13.009%,98.839% 12.718%,98.833% 12.431%,98.83% 12.12%,98.829% 11.756%,98.829% 11.756%,98.811% 10.309%,98.807% 9.074%,98.809% 8.021%,98.815% 7.118%,98.818% 6.334%,98.813% 5.638%,98.796% 5%,98.761% 4.387%,98.704% 3.769%,98.619% 3.116%,98.619% 3.116%,98.528% 2.796%,98.365% 2.493%,98.142% 2.21%,97.872% 1.954%,97.568% 1.729%,97.243% 1.541%,96.909% 1.395%,96.579% 1.296%,96.267% 1.25%,95.984% 1.262%,95.984% 1.262%,95.594% 1.313%,95.225% 1.345%,94.876% 1.359%,94.545% 1.358%,94.233% 1.345%,93.936% 1.322%,93.655% 1.293%,93.388% 1.259%,93.133% 1.225%,92.889% 1.192%,92.889% 1.192%,92.823% 1.191%,92.673% 1.187%,92.438% 1.182%,92.118% 1.175%,91.713% 1.165%,91.221% 1.154%,90.642% 1.14%,89.974% 1.124%,88.372% 1.087%,88.372% 1.087%,86.969% 1.056%,85.626% 1.028%,84.349% 1.002%,83.144% 0.977%,82.02% 0.955%,80.982% 0.935%,80.038% 0.918%,79.193% 0.902%,78.456% 0.888%,77.832% 0.877%,77.832% 0.877%,75.78% 0.847%,73.821% 0.822%,71.96% 0.799%,70.205% 0.78%,68.563% 0.763%,67.039% 0.748%,65.642% 0.735%,64.377% 0.723%,63.252% 0.712%,62.273% 0.702%,62.273% 0.702%,61.871% 0.702%,61.482% 0.702%,61.055% 0.701%,60.54% 0.7%,59.884% 0.697%,59.036% 0.694%,57.944% 0.69%,56.558% 0.684%,54.825% 0.676%,52.695% 0.667%,52.695% 0.667%,49.186% 0.657%,45.885% 0.65%,42.83% 0.646%,40.06% 0.644%,37.612% 0.645%,35.524% 0.649%,33.834% 0.657%,32.58% 0.668%,31.8% 0.683%,31.532% 0.702%,31.532% 0.702%,31.25% 0.719%,30.403% 0.729%,28.993% 0.733%,27.02% 0.732%,24.484% 0.728%,21.386% 0.722%,17.727% 0.715%,13.506% 0.709%,8.725% 0.704%,3.384% 0.702%,3.384% 0.702%,3.269% 0.699%,3.128% 0.698%,2.966% 0.707%,2.787% 0.735%,2.594% 0.794%,2.393% 0.891%,2.187% 1.036%,1.981% 1.239%,1.779% 1.509%,1.585% 1.856%,1.585% 1.856%,1.299% 2.576%,1.106% 3.406%,0.988% 4.37%,0.929% 5.49%,0.911% 6.789%,0.917% 8.289%,0.929% 10.014%,0.931% 11.985%,0.904% 14.226%,0.833% 16.759%,0.833% 16.759%,0.752% 19.129%,0.646% 21.315%,0.527% 23.363%,0.41% 25.317%,0.31% 27.223%,0.239% 29.126%,0.213% 31.071%,0.245% 33.103%,0.349% 35.267%,0.54% 37.608%)",
              backgroundColor: "rgba(255, 255, 255, 0.8)",
              border: "none",
              outline: "none",
            },
          }}
        >
          {milestoneTypes.map((type) => (
            <MenuItem
              key={type.value}
              onClick={() => handleMilestoneSelect(type.follow_up)}
              disabled={userAccess !== "write"}
            >
              {type.follow_up}
            </MenuItem>
          ))}
        </Menu>

        <AddItemDialog
          open={addItemDialogOpen}
          onClose={() => setAddItemDialogOpen(false)}
          onAdd={handleAddItemSubmit}
          milestoneIndex={selectedMilestoneIndex}
        />
      </TimelineContainer>
    </LocalizationProvider>
  );
};

export default FollowUpSection;
